"use client"

import { observer } from "mobx-react-lite"
import { useState, useEffect } from "react"
import { User, GraduationCap, FileText, Edit, Plus, ExternalLink } from "lucide-react"
import ErrorBoundary from "../../../shared/components/error-boundary/ErrorBoundary"
import { useAppContext } from "../../../shared/functions/Context"
import { defaultPersonalDevelopment, type IPersonalDevelopment } from "../../../shared/models/PersonalDevelopment"
import Modal from "../../../shared/components/Modal"
import MODAL_NAMES from "../../dialogs/ModalName"
import showModalFromId from "../../../shared/functions/ModalShow"
import UpdatePersonalDevelopmentModal from "../../dialogs/personal-development/UpdatePersonalDevelopmentModal"
import AddPersonalQualification from "../../dialogs/personal-development/AddPersonalQualification"
import type Qualification from "../../../shared/models/Qualifications"
import { Button } from "../../dashboard/company-dashboard/ui/button"
import { Badge } from "../../dashboard/company-dashboard/ui/badge"
import { Separator } from "../../dashboard/company-dashboard/ui/separator"


const PersonalDevelopmentProfile = observer(() => {
  const { api, store, ui } = useAppContext()
  const [loading, setLoading] = useState(false)
  const [profile, setProfile] = useState<IPersonalDevelopment>({
    ...defaultPersonalDevelopment,
  })

  const me = store.auth.me?.asJson

  const getCurrentProfile = () => {
    return store.personalDevelopment.all.find(
      (profile) => profile.asJson.uid === me?.uid && profile.asJson.type === "Profile",
    )?.asJson
  }

  const currentProfile = getCurrentProfile()
  const allMyQualifications = store.qualification.all.filter((qualification) => qualification.asJson.uid === me?.uid)

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true)
      try {
        await Promise.all([api.user.getAll(), api.personalDevelopment.getAll(), api.qualifications.getAll()])
        const updatedProfile = getCurrentProfile()
        if (updatedProfile) {
          setProfile(updatedProfile)
        }
      } catch (error) {
        ui.snackbar.load({
          id: Date.now(),
          message: "Failed to load.",
          type: "danger",
        })
      }
      setLoading(false)
    }

    loadAll()
  }, [api, ui.snackbar])

  useEffect(() => {
    const updatedProfile = getCurrentProfile()
    if (updatedProfile) {
      setProfile(updatedProfile)
    }
  }, [store.personalDevelopment.all, me?.uid])

  const handleAddAcademic = () => {
    store.personalDevelopment.select(profile)
    showModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_QUALIFICATIONS_MODAL)
  }

  const handleUpdate = () => {
    store.personalDevelopment.select(profile)
    showModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_UPDATE_MODAL)
  }

  const handleQualificationClick = (qualification: Qualification) => {
    store.qualification.select(qualification.asJson)
    showModalFromId(MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_QUALIFICATIONS_MODAL)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#84cbe9]"></div>
      </div>
    )
  }

  return (
    <ErrorBoundary>
      <div className="w-full space-y-6">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center">
                <User className="h-6 w-6 text-[#84cbe9]" />
              </div>
              <h2 className="text-2xl font-bold text-[#3f6a8e]">Personal Development Profile</h2>
            </div>
            <Button className="bg-[#84cbe9] hover:bg-[#6ab5d4] text-white" onClick={handleUpdate}>
              <Edit className="h-4 w-4 mr-2" />
              Edit Profile
            </Button>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Career Goals</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md min-h-[60px] border border-gray-200">
                  {profile.careerGoals || "Not specified"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Skill Development</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md min-h-[60px] border border-gray-200">
                  {profile.skillDevelopment || "Not specified"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Improvement Areas</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md min-h-[60px] border border-gray-200">
                  {profile.improvementAreas || "Not specified"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Action Items</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md min-h-[60px] border border-gray-200">
                  {profile.actionItems || "Not specified"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Leadership & Values</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md min-h-[60px] border border-gray-200">
                  {profile.leadershipValues || "Not specified"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Skill Improvement & Development Actions</label>
                <p className="text-gray-900 bg-gray-50 p-3 rounded-md min-h-[60px] border border-gray-200">
                  {profile.skillImprovement || "Not specified"}
                </p>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-500">Gender</label>
                <Badge className="bg-blue-100 text-[#84cbe9] hover:bg-blue-200 border-0">
                  {profile.gender || "Not specified"}
                </Badge>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="h-12 w-12 rounded-full bg-yellow-100 flex items-center justify-center">
                <GraduationCap className="h-6 w-6 text-[#e7a637]" />
              </div>
              <h2 className="text-2xl font-bold text-[#3f6a8e]">Qualifications</h2>
            </div>
            <Button className="bg-[#e7a637] hover:bg-[#d89530] text-white" onClick={handleAddAcademic}>
              <Plus className="h-4 w-4 mr-2" />
              Add Qualification
            </Button>
          </div>
          <div>
            {allMyQualifications.length > 0 ? (
              <div className="grid grid-cols-1 gap-4">
                {allMyQualifications.map((qualification) => (
                  <div
                    key={qualification.asJson.id}
                    className="bg-white border border-gray-200 rounded-lg p-6 hover:border-[#84cbe9] hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="space-y-1">
                        <h3 className="text-lg font-bold text-[#3f6a8e]">{qualification.asJson.courseName}</h3>
                        <p className="text-sm text-gray-600">{qualification.asJson.institution}</p>
                      </div>
                      <Button
                        size="sm"
                        className="border-2 border-[#84cbe9] bg-white text-[#84cbe9] hover:bg-blue-50"
                        onClick={() => handleQualificationClick(qualification)}
                      >
                        <Edit className="h-3 w-3 mr-1" />
                        Edit
                      </Button>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <div>
                          <p className="text-xs text-gray-500">Start Date</p>
                          <p className="text-sm font-medium text-gray-900">{qualification.asJson.startDate}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">End Date</p>
                          <p className="text-sm font-medium text-gray-900">{qualification.asJson.endDate}</p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Certificate</p>
                          <p className="text-sm font-medium text-gray-900">
                            {qualification.asJson.certificateObtained}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-gray-500">Status</p>
                          <Badge className="bg-green-100 text-[#4bb543] hover:bg-green-200 border-0">Completed</Badge>
                        </div>
                      </div>

                      {qualification.asJson.comments && (
                        <>
                          <Separator className="bg-gray-200" />
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Comments</p>
                            <p className="text-sm text-gray-900">{qualification.asJson.comments}</p>
                          </div>
                        </>
                      )}

                      {qualification.asJson.attachment && qualification.asJson.attachment.length > 0 && (
                        <>
                          <Separator className="bg-gray-200" />
                          <div>
                            <p className="text-xs text-gray-500 mb-2 flex items-center gap-2">
                              <FileText className="h-3 w-3" />
                              Attachments
                            </p>
                            <div className="flex flex-wrap gap-2">
                              {qualification.asJson.attachment.map((url: string, index: number) => (
                                <Button
                                  key={index}
                                  size="sm"
                                  className="text-xs border-2 border-gray-300 bg-white text-gray-700 hover:bg-gray-50"
                                  onClick={() => window.open(url, "_blank", "noopener,noreferrer")}
                                >
                                  <ExternalLink className="h-3 w-3 mr-1" />
                                  Attachment {index + 1}
                                </Button>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="h-16 w-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-4">
                  <GraduationCap className="h-8 w-8 text-[#e7a637]" />
                </div>
                <p className="text-gray-600 mb-4">No qualifications added yet.</p>
                <Button
                  onClick={handleAddAcademic}
                  className="border-2 border-[#e7a637] bg-white text-[#e7a637] hover:bg-yellow-50"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add Your First Qualification
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal modalId={MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_UPDATE_MODAL}>
        <UpdatePersonalDevelopmentModal />
      </Modal>
      <Modal modalId={MODAL_NAMES.EXECUTION.PERSONAL_DEVELOPMENT_QUALIFICATIONS_MODAL}>
        <AddPersonalQualification />
      </Modal>
    </ErrorBoundary>
  )
})

export default PersonalDevelopmentProfile
